// Production-ready logger utility.
// Verbosity is controlled by a single flag: VITE_ENABLE_DEBUG_LOGGING.
// - errors and warnings always log
// - info / debug / api / auth / performance only log when the flag is "true"
// Set it in frontend/.env when you need the chatter; leave it false for a
// clean console in both dev and prod.
const IS_PRODUCTION = import.meta.env.PROD;
const ENABLE_DEBUG_LOGGING = import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true';

class Logger {
  constructor() {
    this.isProduction = IS_PRODUCTION;
    this.enableDebug = ENABLE_DEBUG_LOGGING;
  }

  // Info — gated by the debug flag.
  info(message, data = null) {
    if (this.enableDebug) {
      console.log(`[INFO] ${message}`, data || '');
    }
  }

  // Warning — always enabled.
  warn(message, data = null) {
    console.warn(`[WARN] ${message}`, data || '');
  }

  // Error — always enabled; optionally ships to the logging service in prod.
  error(message, error = null) {
    console.error(`[ERROR] ${message}`, error || '');
    if (this.isProduction) {
      this.sendToLoggingService('error', message, error);
    }
  }

  // Debug — gated by the debug flag.
  debug(message, data = null) {
    if (this.enableDebug) {
      console.debug(`[DEBUG] ${message}`, data || '');
    }
  }

  apiRequest(method, url, data = null) {
    if (this.enableDebug) {
      console.log(`[API] ${method.toUpperCase()} ${url}`, data || '');
    }
  }

  apiResponse(method, url, status, data = null) {
    if (this.enableDebug) {
      const color = status >= 400 ? 'red' : 'green';
      console.log(`%c[API] ${method.toUpperCase()} ${url} - ${status}`, `color: ${color}`, data || '');
    }
  }

  auth(action, success = true, details = null) {
    if (this.enableDebug) {
      const status = success ? 'SUCCESS' : 'FAILED';
      console.log(`[AUTH] ${action} - ${status}`, details || '');
    }
  }

  performance(operation, duration) {
    if (this.enableDebug) {
      console.log(`[PERF] ${operation} took ${duration}ms`);
    }
  }

  // Send to external logging service (for production)
  sendToLoggingService(level, message, error = null) {
    // Implement your logging service integration here
    // Examples: Sentry, LogRocket, DataDog, etc.
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'error', {
        error_message: message,
        error_stack: error?.stack,
        error_level: level
      });
    }
  }
}

export const logger = new Logger();
export default logger;
